<template>
  <span class="forwards-country-pill" :title="countryNames">
    <span aria-hidden="true">{{ flags }}</span>
    <span class="fw-semibold">{{ label }}</span>
    <span
      v-if="volumeMwh !== null && volumeMwh !== undefined"
      class="text-body-tertiary"
    >
      {{
        $t("forwards.common.mwh_value", {
          value: formatNumberWithSpaces(volumeMwh),
        })
      }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    /**
     * ISO 3166-1 alpha-2 code, as `get_registry_country_for_device` returns it — or several,
     * for a volume a multi-country pool may fill from any of its countries ("EE/LV 640").
     */
    country: string | string[];
    /** MWh attributed to this country; omit for a bare country marker. */
    volumeMwh?: number | null;
  }>(),
  { volumeMwh: null }
);

const { t, te } = useI18n();

const codes = computed(() =>
  (Array.isArray(props.country) ? props.country : [props.country]).map((code) =>
    code.toUpperCase()
  )
);
const label = computed(() => codes.value.join("/"));
const flags = computed(() => codes.value.map(getFlagEmoji).join(""));
const countryNames = computed(() =>
  codes.value.map((code) => translateCountryCode(code, t, te)).join(" / ")
);
</script>

<style scoped lang="scss">
.forwards-country-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0.0625rem 0.375rem;
  border-radius: 999px;
  background: var(--phoenix-gray-100);
  font-size: 0.75rem;
  white-space: nowrap;
}
</style>
